import { type ChangeEvent, type InputHTMLAttributes, type ReactNode, useId, useState } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
	/**
	 * Подпись живёт внутри поля и уезжает наверх при фокусе или заполнении.
	 * Отдельной подписи над полем в системе нет — она съедала бы вертикаль,
	 * которой в формах входа и так мало.
	 */
	label?: string;
	/** `md` — 64px r12 (основной), `sm` — компактный r8 для плотных форм. */
	size?: "md" | "sm";
	iconBefore?: ReactNode;
	iconAfter?: ReactNode;
	errorText?: ReactNode;
	hasError?: boolean;
	hint?: ReactNode;
	/**
	 * Шаблон формата внутри поля (`ДД.ММ.ГГГГ`). Виден при фокусе и наборе:
	 * недописанные позиции остаются серыми. Без фокуса и значения прячется,
	 * чтобы не перекрывать плавающую подпись.
	 */
	formatGuide?: string;
	/**
	 * Подписи переключателя видимости для `type="password"`.
	 *
	 * Без них переключатель не рисуется вовсе: текстовых умолчаний у кита нет
	 * (аудит В1), а кнопка без доступного имени хуже отсутствующей кнопки.
	 * В IEC ID пароля нет (декрет Д1), поэтому проп нужен только тем, кто
	 * встраивает кит в чужую форму.
	 */
	revealLabels?: { show: string; hide: string };
}

interface FieldGeometry {
	size: "md" | "sm";
	hasLabel: boolean;
	hasIconBefore: boolean;
	hasTrailing: boolean;
	error: boolean;
}

/**
 * Одна таблица геометрии на поле, шаблон формата и плавающую подпись.
 *
 * Раньше отступы поля и смещения `formatGuide` считались в двух местах
 * независимо, и комбинация `size="sm"` + `label` промахивалась: поле получало
 * паддинг 6px, а подпись — высоту от `md`. Теперь и поле, и шаблон, и подпись
 * берут координаты отсюда, поэтому разъехаться им негде.
 *
 * Значения — из DS: md с подписью 27/17, без подписи 19/19, sm без подписи
 * 6/6; sm с подписью 18/4 — компактная версия той же «подпись над текстом».
 */
const GEOMETRY = {
	md: {
		plain: { padding: "py-[19px]", textTop: "top-[19px]" },
		labeled: {
			padding: "pt-[27px] pb-[17px]",
			textTop: "top-[27px]",
			labelRest: "top-[23px]",
			labelRaised: "top-3.5",
		},
		left: "left-5",
		shape: "rounded-card px-5",
	},
	sm: {
		plain: { padding: "py-1.5", textTop: "top-1.5" },
		labeled: {
			padding: "pt-[18px] pb-1",
			textTop: "top-[18px]",
			labelRest: "top-[11px]",
			labelRaised: "top-1",
		},
		left: "left-2.5",
		shape: "rounded-field-sm px-2.5",
	},
} as const;

/** Где начинается текст поля: отступ слева и сверху. */
function textOffsets(size: "md" | "sm", hasLabel: boolean, hasIconBefore: boolean) {
	const g = GEOMETRY[size];
	return {
		left: hasIconBefore ? "left-10" : g.left,
		top: hasLabel ? g.labeled.textTop : g.plain.textTop,
	};
}

function fieldClasses({
	size,
	hasLabel,
	hasIconBefore,
	hasTrailing,
	error,
}: FieldGeometry): string {
	const g = GEOMETRY[size];
	return cn(
		"w-full bg-sunken text-fg outline-none",
		"border border-transparent transition-[border-color,box-shadow] duration-150 ease-smooth",
		"disabled:cursor-not-allowed disabled:opacity-60",
		"focus:border-brand focus:shadow-[0_0_0_3px_var(--color-ring-halo)]",
		g.shape,
		"t-body",
		hasLabel ? g.labeled.padding : g.plain.padding,
		hasIconBefore && "pl-10",
		hasTrailing && "pr-[50px]",
		error && "border-danger",
	);
}

export function Input({
	label,
	size = "md",
	iconBefore,
	iconAfter,
	errorText,
	hasError,
	hint,
	formatGuide,
	revealLabels,
	className,
	id,
	type = "text",
	value,
	defaultValue,
	placeholder,
	onChange,
	onFocus,
	onBlur,
	disabled,
	required,
	...rest
}: InputProps) {
	const autoId = useId();
	const inputId = id ?? autoId;
	const errorId = `${inputId}-error`;
	const hintId = `${inputId}-hint`;
	const [focused, setFocused] = useState(false);
	const [inner, setInner] = useState(defaultValue ?? "");
	const [revealed, setRevealed] = useState(false);

	const current = value !== undefined ? value : inner;
	const filled = String(current ?? "").length > 0;
	const raised = focused || filled;
	const error = Boolean(hasError || errorText);
	const isPassword = type === "password";
	const guideRemainder = formatGuide ? formatGuide.slice(String(current ?? "").length) : "";
	const showGuide = Boolean(formatGuide) && (focused || filled) && guideRemainder.length > 0;
	const offsets = textOffsets(size, Boolean(label), Boolean(iconBefore));

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (value === undefined) setInner(event.target.value);
		onChange?.(event);
	};

	return (
		<div className={cn("flex w-full flex-col", className)}>
			<div className="relative flex items-center">
				{iconBefore ? (
					<span className="pointer-events-none absolute left-3.5 inline-flex text-fg-muted">
						{iconBefore}
					</span>
				) : null}
				<input
					id={inputId}
					type={isPassword && revealed ? "text" : type}
					value={current}
					placeholder={focused && !formatGuide ? placeholder : undefined}
					disabled={disabled}
					required={required}
					aria-invalid={error || undefined}
					aria-describedby={
						cn(errorText ? errorId : undefined, hint ? hintId : undefined) || undefined
					}
					onChange={handleChange}
					onFocus={(event) => {
						setFocused(true);
						onFocus?.(event);
					}}
					onBlur={(event) => {
						setFocused(false);
						onBlur?.(event);
					}}
					className={fieldClasses({
						size,
						hasLabel: Boolean(label),
						hasIconBefore: Boolean(iconBefore),
						hasTrailing: Boolean(iconAfter) || (isPassword && revealLabels !== undefined),
						error,
					})}
					{...rest}
				/>
				{showGuide ? (
					<span
						aria-hidden
						className={cn(
							"pointer-events-none absolute t-body text-fg-muted",
							offsets.left,
							offsets.top,
						)}
					>
						<span className="invisible">{String(current ?? "")}</span>
						<span>{guideRemainder}</span>
					</span>
				) : null}
				<FloatingLabel
					htmlFor={inputId}
					label={label}
					raised={raised}
					required={Boolean(required)}
					size={size}
					hasIconBefore={Boolean(iconBefore)}
				/>
				<TrailingSlot
					isPassword={isPassword}
					revealed={revealed}
					onToggleReveal={() => setRevealed((v) => !v)}
					iconAfter={iconAfter}
					revealLabels={revealLabels}
				/>
			</div>
			<FieldFooter errorId={errorId} errorText={errorText} hintId={hintId} hint={hint} />
		</div>
	);
}

function FloatingLabel({
	htmlFor,
	label,
	raised,
	required,
	size,
	hasIconBefore,
}: {
	htmlFor: string;
	label: string | undefined;
	raised: boolean;
	required: boolean;
	size: "md" | "sm";
	hasIconBefore: boolean;
}) {
	if (!label) return null;
	const g = GEOMETRY[size].labeled;
	// pointer-events сняты: клик по подписи обязан попасть в поле и поставить
	// курсор, а не быть перехвачен самой подписью. Связь держит htmlFor.
	return (
		<label
			htmlFor={htmlFor}
			className={cn(
				"pointer-events-none absolute text-fg-muted",
				"transition-[top,font-size] duration-[var(--dur-300)] ease-elegant",
				textOffsets(size, true, hasIconBefore).left,
				raised
					? cn(
							g.labelRaised,
							"font-mono text-[10px] uppercase leading-[1.2] tracking-[var(--ls-tight)]",
						)
					: cn(g.labelRest, "t-body"),
			)}
		>
			{label}
			{required ? <span className="text-danger"> *</span> : null}
		</label>
	);
}

function TrailingSlot({
	isPassword,
	revealed,
	onToggleReveal,
	iconAfter,
	revealLabels,
}: {
	isPassword: boolean;
	revealed: boolean;
	onToggleReveal: () => void;
	iconAfter: ReactNode;
	revealLabels: { show: string; hide: string } | undefined;
}) {
	if (isPassword && revealLabels) {
		return (
			<button
				type="button"
				aria-label={revealed ? revealLabels.hide : revealLabels.show}
				onClick={onToggleReveal}
				className={cn(
					"iec-press-glyph tap-target absolute right-3.5 inline-flex text-fg-muted",
					"transition-colors duration-150 ease-smooth hover:text-fg",
					"focus-ring",
				)}
			>
				<Icon name={revealed ? "EyeOff" : "Eye"} size={20} />
			</button>
		);
	}
	if (!iconAfter) return null;
	return (
		<span className="pointer-events-none absolute right-3.5 inline-flex text-fg-muted">
			{iconAfter}
		</span>
	);
}

function FieldFooter({
	errorId,
	errorText,
	hintId,
	hint,
}: {
	errorId: string;
	errorText: ReactNode;
	hintId: string;
	hint: ReactNode;
}) {
	if (errorText) {
		return (
			<span
				id={errorId}
				role="alert"
				className="mt-1.5 flex items-center gap-1 t-meta text-danger"
				style={{ animation: "iec-badge-in var(--dur-react) var(--ease-spring)" }}
			>
				<Icon name="CircleAlert" size={16} />
				{errorText}
			</span>
		);
	}
	if (!hint) return null;
	return (
		<span id={hintId} className="mt-1.5 t-meta text-fg-muted">
			{hint}
		</span>
	);
}
